import { Theme, lighten } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { VARIABLE_TAG_BACKGROUND } from "styles/oneOffColor";
import { paragraphFontFamily } from "styles/theme";

const variableStyle = {
  borderRadius: "0.4rem",
  padding: "0.1rem 0.1rem",
  boxDecorationBreak: "clone",
  cursor: "pointer",
};

const styles = {
  emailEditContainer: { height: { xs: "100svh", lg: "auto" } },
  dialogTitleText: {
    backgroundColor: "yellow.light",
    borderBottom: `1px solid ${grey[300]}`,
  },
  wrapper: {
    overflow: "hidden",
    color: "green.dark",
    "&::-webkit-scrollbar": {
      width: 0,
    },
  },
  borderWrapper: {
    borderColor: grey[300],
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: 4,
  },
  addAttachmentButton: {
    ml: 2,
    mb: 2,
    fontSize: { xs: 12, md: 14 },
    textTransform: { xs: "none", md: "uppercase" },
  },
  normalEditorWrapper: {
    backgroundColor: "white",
  },
  bubbleMenuBar: (inPortfolioEditor: boolean) => ({
    backgroundColor: inPortfolioEditor ? grey[100] : "white",
    width: inPortfolioEditor ? "575px" : "fit-content",
    maxWidth: "100%",
  }),
  menuBar: {
    overflow: "hidden",
  },
  compactMenuBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 0.25,
    px: 1.25,
    py: 0.75,
    borderTop: "1px solid #E5DCC8",
    backgroundColor: "#F4F0E7",
  },
  compactButton: {
    color: "#6B7770",
    fontSize: 14,
    width: 30,
    height: 30,
    borderRadius: "6px",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
      color: "#0E3A2A",
    },
    "&:disabled": {
      color: grey[400],
    },
  },
  button: {
    color: "green.dark",
    fontSize: 14,
    display: "inline-block",
    borderRadius: 0,
    fontFamily: "monospace",
    fontWeight: 800,
    "&:disabled": {
      color: grey[500],
    },
  },
  trashButton: {
    color: red[900],
  },
  active: (theme: Theme) => ({
    color: lighten(theme.palette.primary.main, 0.2),
  }),
  error: {
    ".ProseMirror p.is-editor-empty:first-child::before": {
      color: "red",
    },
  },
  plainEditor: {
    ".ProseMirror": {
      minHeight: "0px",
    },
  },
  scrollableEditorBody: {
    maxHeight: 260,
    overflowY: "auto",
    ".ProseMirror": {
      minHeight: 180,
    },
  },
  subjectEditor: {
    py: 0,
    "&:focus": {
      outline: "none !important",
    },
    ".merge-tag": {
      color: "black",
      background: VARIABLE_TAG_BACKGROUND,
      ...variableStyle,
    },
    ".variable": {
      color: "black",
      background: VARIABLE_TAG_BACKGROUND,
      ...variableStyle,
    },
    fontSize: 14,
    fontFamily: paragraphFontFamily,
    fontWeight: 400,
  },
  emailEditor: (showVariableError?: boolean) => ({
    px: 2,
    py: 0,
    "&:focus": {
      outline: "none !important",
    },
    ...(showVariableError
      ? {
          ".merge-tag": {
            color: "black",
            background: VARIABLE_TAG_BACKGROUND,
            ...variableStyle,
          },
        }
      : {
          ".merge-tag": {
            color: grey[400],
          },
        }),
    ".variable": {
      color: showVariableError ? "black" : grey[400],
      background: showVariableError ? VARIABLE_TAG_BACKGROUND : "none",
      ...variableStyle,
    },
    ".variable[data-subtype='user-specific']": {
      color: "black",
      background: VARIABLE_TAG_BACKGROUND,
      ...variableStyle,
    },
    ".variable[data-subtype='email-specific']": {
      color: showVariableError ? "black" : grey[400],
      background: showVariableError ? VARIABLE_TAG_BACKGROUND : "none",
      ...variableStyle,
    },
    fontSize: 14,
    ".ProseMirror": {
      minHeight: showVariableError ? "300px" : "50px",
      my: 2,
      overflow: "visible",
    },
    ".ProseMirror p:empty::after": {
      content: '""',
    },
  }),
  editor: {
    "mark.suggestion-hl": {
      position: "relative",
      cursor: "pointer",
      borderRadius: "3px",
      padding: "1px 2px",
      margin: "0 -1px",
      transition: "background 0.15s ease",
    },
    "mark.suggestion-hl--error": {
      background: "rgba(192, 57, 43, 0.18)",
      borderBottom: "2px dotted #C0392B",
    },
    "mark.suggestion-hl--tip": {
      background: "rgba(185, 163, 17, 0.22)",
      borderBottom: "2px dotted #B9A311",
    },
    "mark.suggestion-hl--heads-up": {
      background: "rgba(128, 132, 255, 0.18)",
      borderBottom: "2px dotted #8084FF",
    },
    // Black tooltip showing the suggestion type, revealed on hover.
    "mark.suggestion-hl[data-title]::after": {
      content: "attr(data-title)",
      position: "absolute",
      bottom: "calc(100% + 10px)",
      left: 0,
      background: "#151e16",
      color: "#fff",
      fontSize: "11px",
      fontWeight: 500,
      lineHeight: 1.4,
      padding: "5px 10px 5px 20px",
      borderRadius: "8px",
      whiteSpace: "nowrap",
      opacity: 0,
      transform: "translateY(4px)",
      pointerEvents: "none",
      transition: "opacity 0.12s, transform 0.12s",
      zIndex: 50,
    },
    // Colored dot inside the tooltip, keyed to severity.
    "mark.suggestion-hl[data-title]::before": {
      content: '""',
      position: "absolute",
      bottom: "calc(100% + 19px)",
      left: "9px",
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      opacity: 0,
      transform: "translateY(4px)",
      pointerEvents: "none",
      transition: "opacity 0.12s, transform 0.12s",
      zIndex: 51,
    },
    "mark.suggestion-hl--error::before": { background: "#ff7d70" },
    "mark.suggestion-hl--tip::before": { background: "#f5d966" },
    "mark.suggestion-hl--heads-up::before": { background: "#b1b3f8" },
    "mark.suggestion-hl:hover::after, mark.suggestion-hl:hover::before": {
      opacity: 1,
      transform: "translateY(0)",
    },
    ".ProseMirror.pm-hl-bubble-off mark.suggestion-hl::after, .ProseMirror.pm-hl-bubble-off mark.suggestion-hl::before":
      {
        opacity: "0 !important",
      },
    ".lt": {
      borderBottom: "2px solid #e86a69",
      transition: "background 0.25s ease-in-out",
      "&:hover": {
        background: "rgba($color: #e86a69, $alpha: 0.2)",
      },
      "&-style": {
        borderBottom: "2px solid #9d8eff",

        "&:hover": {
          background: "rgba($color: #9d8eff, $alpha: 0.2) !important",
        },
      },

      "&-typographical, &-grammar": {
        borderBottom: "2px solid #eeb55c",

        "&:hover": {
          background: "rgba($color: #eeb55c, $alpha: 0.2) !important",
        },
      },

      "&-misspelling": {
        borderBottom: "2px solid #e86a69",

        "&:hover": {
          background: "rgba($color: #e86a69, $alpha: 0.2) !important",
        },
      },
      "&-focused": {
        outline: "none !important",
      },
    },
    // If you change any CSS here, make sure to also update html_utils.insert_email_css
    table: {
      borderCollapse: "collapse",
      margin: 0,
    },
    "table td": {
      minWidth: "1em",
      boxSizing: "border-box",
      padding: "0",
      position: "relative",
      verticalAlign: "top",
    },
    "table th": {
      minWidth: "1em",
      padding: "0",
      position: "relative",
      verticalAlign: "top",
    },
    "table th > *": {
      marginBottom: 0,
    },
    "table td > *": {
      marginBottom: 0,
    },
    "table .selectedCell:after": {
      background: "rgba(200, 200, 255, 0.4)",
      content: '""',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      pointerEvents: "none",
      position: "absolute",
      zIndex: 2,
    },
    "table .column-resize-handle": {
      backgroundColor: "#adf",
      bottom: "-2px",
      position: "absolute",
      right: "-2px",
      pointerEvents: "none",
      top: 0,
      width: "4px",
    },
    "table p": {
      margin: 0,
    },
    ".tableWrapper": {
      padding: "0",
      overflow: "visible",
    },
    ".resize-cursor": {
      cursor: "col-resize",
    },
  },
  portfolioEditor: (theme: Theme) => ({
    px: 0,
    ".ProseMirror": {
      minHeight: "0px",
      minWidth: "100px",
      ...theme.typography,
      p: {
        ...theme.typography.body1,
      },
      "h1, h2, h3, p": {
        m: 0,
        mb: 1,
      },
    },
  }),
  attachmentContainer: {
    width: "auto",
    display: "flex",
    alignItems: "center",
    ml: 2,
    mr: 8,
    mb: 3,
    color: "text.secondary",
  },
  attachmentName: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  actionButton: {
    width: 75,
  },
  emailContainer: { p: 0, height: "100svh", width: { xs: "auto", md: 500 } },
  emailHeaderContainer: { p: 1 },
  fillVariable: {
    textTransform: "none",
    borderRight: `1px solid ${grey[200]}`,
    borderRadius: 0,
  },
};

export default styles;
